import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Eraser, PenLine, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
};

const SignMovementPage = () => {
  const { token } = useParams();
  const sigPadRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [movement, setMovement] = useState(null);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [justSigned, setJustSigned] = useState(false);

  const fetchMovement = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/public/movement/${token}`);
      setMovement(data);
      if (data.last_signer_name) setSignerName(data.last_signer_name);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo cargar la responsiva');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleClear = () => {
    sigPadRef.current?.clear();
  };

  const handleSubmit = async () => {
    if (!signerName.trim() || signerName.trim().length < 2) {
      toast.error('Ingresa tu nombre completo');
      return;
    }
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error('Dibuja tu firma en el recuadro');
      return;
    }
    setSaving(true);
    try {
      const dataUrl = sigPadRef.current.getTrimmedCanvas().toDataURL('image/png');
      await axios.post(`${BACKEND_URL}/api/public/movement/${token}/sign`, {
        signature_png: dataUrl,
        signer_name: signerName.trim()
      });
      toast.success('¡Firma registrada correctamente!');
      setJustSigned(true);
      await fetchMovement();
      sigPadRef.current?.clear();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo registrar la firma');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4">
        <div className="max-w-md text-center bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-md">
          <div className="text-red-400 text-lg font-semibold mb-2">No se pudo cargar</div>
          <p className="text-sm text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  const typeLabel = movement.type === 'prestamo' ? 'Préstamo' : 'Entrega de Equipo';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 py-8 px-4" data-testid="sign-movement-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-sky-500/10 border border-sky-500/30 mb-4">
            <PenLine className="h-7 w-7 text-sky-400" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Firma digital de responsiva</h1>
          <p className="text-sm text-slate-400 mt-1">CM Idiomas · {typeLabel}</p>
        </div>

        {/* Movement info */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm space-y-2 text-sm">
          {movement.event_name && (
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">Evento</span>
              <span className="font-medium text-right" data-testid="sign-event-name">{movement.event_name}</span>
            </div>
          )}
          {movement.event_date && (
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">Fecha del evento</span>
              <span className="font-medium">{movement.event_date}</span>
            </div>
          )}
          {movement.client_name && (
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">Cliente</span>
              <span className="font-medium text-right">{movement.client_name}</span>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-3 text-slate-300">
            <Package className="h-4 w-4" />
            <span className="text-sm font-semibold">Equipo bajo tu responsabilidad</span>
          </div>
          <div className="divide-y divide-white/5">
            {movement.items?.map((it, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-200">{it.name}</span>
                <span className="font-mono font-semibold text-sky-300">{it.quantity}</span>
              </div>
            ))}
            {(!movement.items || movement.items.length === 0) && (
              <div className="py-2 text-sm text-slate-500">Sin items registrados.</div>
            )}
          </div>
        </div>

        {/* Previous signature notice */}
        {movement.already_signed && !justSigned && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-emerald-300">Esta responsiva ya fue firmada</div>
              <div className="text-slate-400 mt-0.5">
                Última firma: <span className="text-slate-200">{movement.last_signer_name}</span> el {formatDate(movement.last_signed_at)}. Puedes volver a firmar si lo necesitas.
              </div>
            </div>
          </div>
        )}

        {/* Signature pad */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm space-y-4">
          <div>
            <Label htmlFor="signer_name" className="text-slate-300">Tu nombre</Label>
            <Input
              id="signer_name"
              data-testid="sign-signer-name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Ej. Coord. María López"
              className="mt-1 bg-slate-900 border-white/10 text-slate-100 placeholder:text-slate-500"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-300">Firma</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="text-slate-400 hover:text-slate-200 h-7"
                data-testid="sign-clear-btn"
              >
                <Eraser className="h-3.5 w-3.5 mr-1" />
                Limpiar
              </Button>
            </div>
            <div className="rounded-xl border-2 border-dashed border-white/20 bg-white overflow-hidden">
              <SignatureCanvas
                ref={sigPadRef}
                canvasProps={{
                  className: 'w-full h-44 touch-none',
                  'data-testid': 'sign-canvas'
                }}
                penColor="#0f172a"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">Dibuja con el dedo dentro del recuadro blanco.</p>
          </div>
          <Button
            className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold"
            onClick={handleSubmit}
            disabled={saving}
            data-testid="sign-submit-btn"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {movement.already_signed ? 'Re-firmar responsiva' : 'Firmar responsiva'}
          </Button>
        </div>

        <p className="text-center text-xs text-slate-500">
          Al firmar, reconoces la recepción del equipo listado bajo responsabilidad de <span className="text-slate-300">{movement.client_name || 'tu empresa'}</span>.
        </p>
      </div>
    </div>
  );
};

export default SignMovementPage;
